import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MY_DATE_FORMATS } from '../../news/edit-detail-incorporate/edit-detail-incorporate.component';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { PostPrice } from 'src/app/shared/interfaces/restaurant.interface';

interface MealTypeOption {
  display: string;
  value: string;
}

@Component({
  selector: 'app-price-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CommonModule,
    MatIconModule,
    RouterLink,
    LoaderComponent,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './price-form.component.html',
  styleUrls: [
    './price-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class PriceFormComponent implements OnInit {
  priceForm: FormGroup;
  priceId: string | null = null;
  isEditMode: boolean = false;
  isSubmitting: boolean = false;
  myroles: any = {};
  errorMessage = signal('');

  mealTypeOptions: MealTypeOption[] = [
    { display: 'Desayuno', value: 'BREAKFAST' },
    { display: 'Comida', value: 'LUNCH' },
  ];

  private _snackBar = inject(MatSnackBar);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private restaurantService: RestaurantService,
    private loaderService: LoaderService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.priceForm = this.fb.group({
      price: [
        '',
        [Validators.required, Validators.min(1), Validators.max(99.99)],
      ],
      price_company: [
        '',
        [Validators.required, Validators.min(0), Validators.max(99.99)],
      ],
      mealType: ['', [Validators.required]],
      validFrom: ['', [Validators.required]],
      validUntil: [''],
    });
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar precio' : 'Añadir precio';
  }

  get submitButtonText(): string {
    if (this.isSubmitting) {
      return 'Guardando...';
    }
    return 'Guardar cambios';
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    this.route.params.subscribe((params) => {
      this.priceId = params['id'] || null;
      this.isEditMode = !!this.priceId;

      if (this.isEditMode && this.priceId) {
        this.loadPriceData();
      }
    });

    this.loadRoles();

    // Clear error message when form values change
    this.priceForm.valueChanges.subscribe(() => {
      if (this.errorMessage()) {
        this.errorMessage.set('');
      }
    });
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'prices',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      // Check write permission and redirect if needed
      const errorMessage = this.isEditMode
        ? 'No tienes permisos para editar un precio'
        : 'No tienes permisos para añadir un precio';

      this.rolesService.checkWritePermission(
        'prices',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/dining/prices',
        errorMessage
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  loadPriceData() {
    if (!this.priceId) return;

    this.loaderService.showLoader();
    this.restaurantService.getRestaurantPrice(this.priceId).subscribe({
      next: (price) => {
        if (price) {
          // Convert string dates to Date objects for the date pickers
          const validFrom = price.valid_from
            ? new Date(price.valid_from)
            : null;
          const validUntil = price.valid_until
            ? new Date(price.valid_until)
            : null;

          this.priceForm.patchValue({
            price: price.price,
            price_company: price.price_company,
            mealType: price.meal_type,
            validFrom: validFrom,
            validUntil: validUntil,
          });
        }
        this.loaderService.hideLoader();
      },
      error: (err) => {
        console.error('Error loading price data:', err);
        this._snackBar.open('Error al cargar los datos del precio', 'Cerrar', {
          duration: 3000,
        });
        this.loaderService.hideLoader();
      },
    });
  }

  validateDates(): boolean {
    const validFrom = this.priceForm.get('validFrom')?.value;
    const validUntil = this.priceForm.get('validUntil')?.value;

    if (validUntil && validFrom) {
      if (validUntil <= validFrom) {
        this._snackBar.open(
          'La fecha de fin debe ser posterior a la fecha de inicio',
          'Cerrar',
          { duration: 3000 }
        );
        return false;
      }
    }
    return true;
  }

  formatDate(date: Date | null): string | null {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  saveChanges() {
    if (this.isSubmitting) return;

    if (this.priceForm.invalid) {
      this._snackBar.open(
        'Por favor, complete todos los campos requeridos correctamente',
        'Cerrar',
        { duration: 3000 }
      );
      this.priceForm.markAllAsTouched();
      return;
    }

    if (!this.validateDates()) {
      return;
    }

    this.isSubmitting = true;
    this.loaderService.showLoader();

    const formData = this.priceForm.value;
    const priceData: PostPrice = {
      meal_type: formData.mealType,
      price: formData.price,
      price_company: formData.price_company,
      valid_from: this.formatDate(formData.validFrom)!,
      valid_until: formData.validUntil
        ? this.formatDate(formData.validUntil)
        : null,
    };

    const apiCall = this.isEditMode
      ? this.restaurantService.putRestaurantPrice(priceData, this.priceId!)
      : this.restaurantService.postRestaurantPrice(priceData);

    apiCall.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.loaderService.hideLoader();
        this.errorMessage.set('');

        const successMessage = this.isEditMode
          ? 'Precio actualizado correctamente'
          : 'Precio creado con éxito';

        this._snackBar.open(successMessage, 'Cerrar', { duration: 3000 });
        this.router.navigate(['/home/dining/prices']);
      },
      error: (error) => {
        console.error('There was an error!', error);
        this.isSubmitting = false;
        this.loaderService.hideLoader();

        // Extract error message from backend response
        let message = '';
        if (error.error && error.error.detail) {
          message = error.error.detail;
        } else if (
          error.error &&
          error.error.errors &&
          error.error.errors.non_field_errors
        ) {
          message = error.error.errors.non_field_errors[0];
        } else if (error.error && typeof error.error === 'object') {
          // Handle validation errors from API (for edit mode)
          for (const field in error.error) {
            if (error.error[field] && error.error[field].length > 0) {
              message = `${field}: ${error.error[field][0]}`;
              break;
            }
          }
        } else if (error.message) {
          message = error.message;
        } else {
          message = this.isEditMode
            ? 'Error al actualizar el precio. Inténtalo de nuevo.'
            : 'Error al crear el precio. Inténtalo de nuevo.';
        }

        this.errorMessage.set(message);
        this._snackBar.open(message, 'Cerrar', { duration: 5000 });
      },
    });
  }
}
